'use client'

import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import type { Contributor } from '@package/cms/types'
import cn from '@config/tailwind/cn'

interface AuthorHoverCardProps {
  author: Contributor | Partial<Contributor>
  children: React.ReactNode
  className?: string
}

export function AuthorHoverCard({ author, children, className }: AuthorHoverCardProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<'top' | 'bottom'>('bottom')

  const avatar = typeof author.avatar === 'string' ? null : author.avatar
  const name = author.name ?? ''

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    setPosition(spaceBelow < 200 ? 'top' : 'bottom')
    setIsOpen(true)
  }

  return (
    <div
      className={cn('relative inline-block', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsOpen(false)}
    >
      {children}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: position === 'bottom' ? -8 : 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: position === 'bottom' ? -8 : 8, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className={cn(
              'srd:bg-neutrals-white srd:border-neutrals-n-20 srd:absolute srd:z-50 srd:w-72 srd:rounded-xl srd:border srd:p-4 srd:shadow-2xl',
              position === 'bottom' ? 'srd:top-full srd:mt-2' : 'srd:bottom-full srd:mb-2',
              'srd:left-0',
            )}
          >
            {/* Header */}
            <div className='srd:flex srd:items-start srd:justify-between'>
              <div className='srd:flex srd:items-center srd:gap-3'>
                {avatar?.url ? (
                  <img
                    loading='lazy'
                    src={avatar.url}
                    alt={name}
                    width={48}
                    height={48}
                    className='srd:h-12 srd:w-12 srd:rounded-full srd:object-cover'
                  />
                ) : (
                  <div className='srd:bg-main-blue-100 srd:flex srd:h-12 srd:w-12 srd:items-center srd:justify-center srd:rounded-full'>
                    <span className='srd:text-neutrals-white srd:text-lg srd:font-bold'>
                      {name ? name.charAt(0).toUpperCase() : '?'}
                    </span>
                  </div>
                )}
                <div>
                  <h4 className='srd:text-complementary-black srd:font-semibold'>{name || 'Author'}</h4>
                  <p className='srd:text-neutrals-n-75 srd:text-sm'>Kontributor</p>
                </div>
              </div>
            </div>

            {/* Bio */}
            {author.bio && (
              <p className='srd:text-neutrals-n-75 srd:line-clamp-3 srd:text-sm srd:leading-relaxed'>{author.bio}</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
